import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Check } from 'lucide-react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Autocomplete } from '@react-google-maps/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import api from '../lib/api';

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());

export const RSVPPage = () => {
  const { t, language } = useLanguage();
  const { guest, updateGuest } = useAuth();
  const { isLoaded: mapsLoaded } = useGoogleMaps();
  const autocompleteRef = useRef(null);

  // Shared loading state
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [rsvpData, setRsvpData] = useState(null); // null = no RSVP yet

  // --- Contact section ---
  // 'confirmed' = contact on file (show confirmation + Update button)
  // 'form'      = no contact on file, or user clicked Update (show form)
  const contactOnFile = !!(guest?.email && guest?.whatsapp && guest?.postal_address);
  const [contactMode, setContactMode] = useState(() => contactOnFile ? 'confirmed' : 'form');
  const [contactHadPrevious, setContactHadPrevious] = useState(contactOnFile); // whether to show Cancel in form
  const [contactFormData, setContactFormData] = useState({ email: '', whatsapp: '', postalAddress: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [whatsappError, setWhatsappError] = useState(false);
  const [postalAddressError, setPostalAddressError] = useState(false);

  // --- Attendance section ---
  // 'display' = attendance on file (show summary + Modify button)
  // 'form'    = no attendance yet, or user clicked Modify
  const [attendanceMode, setAttendanceMode] = useState('form');
  const [attendanceFormData, setAttendanceFormData] = useState({
    friday: '',
    saturday: '',
    sunday: '',
    dietaryRestrictions: '',
    specialRequests: '',
    questions: '',
  });
  const [attendanceSubmitting, setAttendanceSubmitting] = useState(false);
  const [dayErrors, setDayErrors] = useState({ friday: false, saturday: false, sunday: false });
  const [dietaryError, setDietaryError] = useState(false);

  // Fetch existing RSVP on mount (once)
  useEffect(() => {
    const fetchRsvp = async () => {
      try {
        const res = await api.get('/api/rsvp/me');
        const data = res.data;
        setRsvpData(data);

        // Attendance: on file if any day answer exists
        const attendanceOnFile = data.friday || data.saturday || data.sunday;
        setAttendanceMode(attendanceOnFile ? 'display' : 'form');
      } catch (err) {
        if (err.response?.status === 404) {
          setAttendanceMode('form');
        } else {
          setFetchError(true);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchRsvp();
  }, []);

  // --- Contact handlers ---
  const handleContactUpdate = () => {
    // Intentionally NOT pre-filling — privacy property
    setContactFormData({ email: '', whatsapp: '', postalAddress: '' });
    setEmailError(false);
    setWhatsappError(false);
    setPostalAddressError(false);
    setContactMode('form');
  };

  const handleContactCancel = () => {
    setEmailError(false);
    setWhatsappError(false);
    setPostalAddressError(false);
    setContactMode('confirmed');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    const emailInvalid = !isValidEmail(contactFormData.email);
    const whatsappInvalid = !contactFormData.whatsapp || !isValidPhoneNumber(contactFormData.whatsapp);
    const postalInvalid = !contactFormData.postalAddress.trim();

    setEmailError(emailInvalid);
    setWhatsappError(whatsappInvalid);
    setPostalAddressError(postalInvalid);

    if (emailInvalid || whatsappInvalid || postalInvalid) {
      const selector = emailInvalid
        ? '[data-testid="contact-email-input"]'
        : whatsappInvalid
          ? '[data-testid="contact-whatsapp-input"] input'
          : '[data-testid="contact-postal-input"]';
      const el = document.querySelector(selector);
      if (el) {
        el.focus?.();
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setContactSubmitting(true);
    try {
      const res = await api.put('/api/guests/me', {
        email: contactFormData.email,
        whatsapp: contactFormData.whatsapp,
        postal_address: contactFormData.postalAddress,
      });
      updateGuest(res.data);
      setContactHadPrevious(true);
      setContactMode('confirmed');
    } catch {
      // Could add error toast here
    } finally {
      setContactSubmitting(false);
    }
  };

  // --- Attendance handlers ---
  const handleAttendanceModify = () => {
    setAttendanceFormData({
      friday: rsvpData?.friday || '',
      saturday: rsvpData?.saturday || '',
      sunday: rsvpData?.sunday || '',
      dietaryRestrictions: rsvpData?.dietary_restrictions || '',
      specialRequests: rsvpData?.special_requests || '',
      questions: rsvpData?.questions || '',
    });
    setDayErrors({ friday: false, saturday: false, sunday: false });
    setDietaryError(false);
    setAttendanceMode('form');
  };

  const handleAttendanceCancel = () => {
    setDayErrors({ friday: false, saturday: false, sunday: false });
    setDietaryError(false);
    setAttendanceMode('display');
  };

  const handleRadioChange = (name, value) => {
    setAttendanceFormData((prev) => ({ ...prev, [name]: value }));
    if (dayErrors[name]) setDayErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();

    const newDayErrors = {
      friday: !attendanceFormData.friday,
      saturday: !attendanceFormData.saturday,
      sunday: !attendanceFormData.sunday,
    };
    const dietaryInvalid = !attendanceFormData.dietaryRestrictions.trim();

    setDayErrors(newDayErrors);
    setDietaryError(dietaryInvalid);

    if (newDayErrors.friday || newDayErrors.saturday || newDayErrors.sunday || dietaryInvalid) {
      const selector = newDayErrors.friday
        ? '[data-testid="rsvp-friday-error"]'
        : newDayErrors.saturday
          ? '[data-testid="rsvp-saturday-error"]'
          : newDayErrors.sunday
            ? '[data-testid="rsvp-sunday-error"]'
            : '[data-testid="rsvp-dietary-input"]';
      const el = document.querySelector(selector);
      if (el) {
        el.focus?.();
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setAttendanceSubmitting(true);
    try {
      const res = await api.put('/api/rsvp/me', {
        friday: attendanceFormData.friday,
        saturday: attendanceFormData.saturday,
        sunday: attendanceFormData.sunday,
        dietary_restrictions: attendanceFormData.dietaryRestrictions,
        special_requests: attendanceFormData.specialRequests || null,
        questions: attendanceFormData.questions || null,
      });
      setRsvpData((prev) => ({ ...prev, ...res.data }));
      setAttendanceMode('display');
    } catch {
      // Could add error toast here
    } finally {
      setAttendanceSubmitting(false);
    }
  };

  // --- Shared UI ---
  const twoOptions = [
    { value: 'Yes', label: t('rsvpYes') },
    { value: 'No', label: t('rsvpNo') },
  ];

  const RadioGroup = ({ name, value, onChange, label, options, error }) => (
    <div className="mb-8">
      <label className="block font-serif text-lg text-[#2D2A26] mb-1">
        {label} <span className="text-[#C2734F]">*</span>
      </label>
      <div className="flex flex-wrap gap-3 mt-3">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(name, option.value)}
            className={`px-5 py-2.5 rounded-full border text-sm transition-all ${
              value === option.value
                ? 'bg-[#B5985A] text-white border-[#B5985A]'
                : error
                  ? 'border-[#C2734F] text-[#666] hover:border-[#B5985A] hover:text-[#B5985A]'
                  : 'border-[#DDD] text-[#666] hover:border-[#B5985A] hover:text-[#B5985A]'
            }`}
            data-testid={`rsvp-${name}-${option.value.toLowerCase()}`}
          >
            {option.label}
          </button>
        ))}
      </div>
      {error && (
        <p className="text-[#C2734F] text-sm mt-2" data-testid={`rsvp-${name}-error`}>
          {t('rsvpDayRequired')}
        </p>
      )}
    </div>
  );

  const DisplayRow = ({ label, value }) => (
    <div className="flex justify-between items-baseline py-2 border-b border-[#E6E2D8] last:border-0">
      <span className="text-[#69635A] text-sm">{label}</span>
      <span className="text-[#69635A] text-sm">{value || '—'}</span>
    </div>
  );

  const AttendanceBadge = ({ label, value }) => {
    const isYes = value === 'Yes';
    const isNo = value === 'No';
    return (
      <div className="flex justify-between items-center py-2 border-b border-[#E6E2D8] last:border-0">
        <span className="text-[#69635A] text-sm">{label}</span>
        <span
          className={`text-sm font-medium px-3 py-0.5 rounded-full ${
            isYes
              ? 'bg-[#B5985A]/10 text-[#B5985A]'
              : isNo
                ? 'bg-[#C2734F]/10 text-[#C2734F]'
                : 'text-[#999]'
          }`}
        >
          {value ? t(`rsvp${value}`) : '—'}
        </span>
      </div>
    );
  };

  const SectionHeader = ({ title, action, onAction }) => (
    <div className="flex items-center justify-between mb-6">
      <h2 className="font-serif text-xl text-[#2D2A26]">{title}</h2>
      {action && (
        <button
          type="button"
          onClick={onAction}
          className="text-base text-[#B5985A] hover:text-[#9A7F48] transition-colors cursor-pointer"
        >
          {action}
        </button>
      )}
    </div>
  );

  return (
    <section className="pt-14 pb-24 bg-white min-h-screen" data-testid="rsvp-page">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2A26] mb-4">
            {t('rsvpTitle')}
          </h1>
          <div className="w-12 h-0.5 bg-[#B5985A] mx-auto mb-6" />
          <p className="text-[#666] leading-relaxed">{t('rsvpDescription')}</p>
          <p className="text-[#999] text-sm mt-3">{t('rsvpOnePerPerson')}</p>
        </motion.div>

        {/* Loading */}
        <AnimatePresence>
          {loading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20"
            >
              <div className="w-8 h-8 border-2 border-[#B5985A] border-t-transparent rounded-full animate-spin mx-auto" />
            </motion.div>
          )}
        </AnimatePresence>

        {!loading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            {fetchError && (
              <p className="text-[#C2734F] text-sm text-center">{t('rsvpFetchError')}</p>
            )}

            {/* ── Section 1: Contact Details ── */}
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm p-8 sm:p-10" data-testid="contact-section">
              <AnimatePresence mode="wait">
                {/* Confirmed state */}
                {contactMode === 'confirmed' && (
                  <motion.div
                    key="contact-confirmed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SectionHeader
                      title={t('rsvpContactTitle')}
                      action={t('rsvpContactUpdate')}
                      onAction={handleContactUpdate}
                    />
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#B5985A] flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-[#69635A] text-sm">{t('rsvpContactOnFile')}</p>
                    </div>
                  </motion.div>
                )}

                {/* Form state */}
                {contactMode === 'form' && (
                  <motion.form
                    key="contact-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={handleContactSubmit}
                    noValidate
                    autoComplete="off"
                    data-testid="contact-form"
                  >
                    <SectionHeader title={t('rsvpContactTitle')} />

                    <p className="text-center font-serif text-2xl sm:text-3xl text-[#2D2A26] mb-2">
                      {guest?.first_name} {guest?.last_name}
                    </p>
                    <p className="font-serif text-[#C2734F] text-sm mb-6">{t('rsvpRequiredNote')}</p>

                    {/* Email */}
                    <div className="mb-6">
                      <label className="block font-serif text-lg text-[#2D2A26] mb-2">
                        {t('rsvpEmail')} <span className="text-[#C2734F]">*</span>
                      </label>
                      <input
                        type="email"
                        value={contactFormData.email}
                        onChange={(e) => {
                          setContactFormData((prev) => ({ ...prev, email: e.target.value }));
                          if (emailError) setEmailError(false);
                        }}
                        placeholder={t('rsvpEmailPlaceholder')}
                        className={`w-full border-b py-3 px-1 text-[#2D2A26] placeholder-[#BBB] focus:outline-none transition-colors bg-transparent ${
                          emailError
                            ? 'border-[#C2734F] focus:border-[#C2734F]'
                            : 'border-[#DDD] focus:border-[#B5985A]'
                        }`}
                        data-testid="contact-email-input"
                        aria-invalid={emailError}
                      />
                      {emailError && (
                        <p className="text-[#C2734F] text-sm mt-2" data-testid="contact-email-error">
                          {t('rsvpEmailInvalid')}
                        </p>
                      )}
                    </div>

                    {/* WhatsApp */}
                    <div className="mb-6">
                      <label className="block font-serif text-lg text-[#2D2A26] mb-2">
                        {t('rsvpWhatsapp')} <span className="text-[#C2734F]">*</span>
                      </label>
                      <div
                        data-testid="contact-whatsapp-input"
                        className={`flex items-center border-b py-2 transition-colors ${
                          whatsappError ? 'border-[#C2734F]' : 'border-[#DDD] focus-within:border-[#B5985A]'
                        }`}
                      >
                        <PhoneInput
                          international
                          key={language}
                          defaultCountry={language === 'fr' ? 'FR' : 'US'}
                          value={contactFormData.whatsapp}
                          onChange={(value) => {
                            setContactFormData((prev) => ({ ...prev, whatsapp: value || '' }));
                            if (whatsappError) setWhatsappError(false);
                          }}
                          className="phone-input-wrapper w-full"
                        />
                      </div>
                      {whatsappError && (
                        <p className="text-[#C2734F] text-sm mt-2" data-testid="contact-whatsapp-error">
                          {t('rsvpWhatsappInvalid')}
                        </p>
                      )}
                    </div>

                    {/* Postal Address */}
                    <div className="mb-6">
                      <label className="block font-serif text-lg text-[#2D2A26] mb-2">
                        {t('rsvpPostalAddress')} <span className="text-[#C2734F]">*</span>
                      </label>
                      {mapsLoaded ? (
                        <Autocomplete
                          onLoad={(ac) => { autocompleteRef.current = ac; }}
                          onPlaceChanged={() => {
                            if (autocompleteRef.current) {
                              const place = autocompleteRef.current.getPlace();
                              const address = place.formatted_address || place.name || '';
                              setContactFormData((prev) => ({ ...prev, postalAddress: address }));
                              if (postalAddressError) setPostalAddressError(false);
                            }
                          }}
                        >
                          <input
                            type="text"
                            value={contactFormData.postalAddress}
                            onChange={(e) => {
                              setContactFormData((prev) => ({ ...prev, postalAddress: e.target.value }));
                              if (postalAddressError) setPostalAddressError(false);
                            }}
                            placeholder={t('rsvpPostalAddressPlaceholder')}
                            className={`w-full border-b py-3 px-1 text-[#2D2A26] placeholder-[#BBB] focus:outline-none transition-colors bg-transparent ${
                              postalAddressError
                                ? 'border-[#C2734F] focus:border-[#C2734F]'
                                : 'border-[#DDD] focus:border-[#B5985A]'
                            }`}
                            data-testid="contact-postal-input"
                            aria-invalid={postalAddressError}
                          />
                        </Autocomplete>
                      ) : (
                        <textarea
                          value={contactFormData.postalAddress}
                          onChange={(e) => {
                            setContactFormData((prev) => ({ ...prev, postalAddress: e.target.value }));
                            if (postalAddressError) setPostalAddressError(false);
                          }}
                          placeholder={t('rsvpPostalAddressPlaceholder')}
                          rows={2}
                          className={`w-full border-b py-3 px-1 text-[#2D2A26] placeholder-[#BBB] focus:outline-none transition-colors bg-transparent resize-none ${
                            postalAddressError
                              ? 'border-[#C2734F] focus:border-[#C2734F]'
                              : 'border-[#DDD] focus:border-[#B5985A]'
                          }`}
                          data-testid="contact-postal-input"
                          aria-invalid={postalAddressError}
                        />
                      )}
                      {postalAddressError && (
                        <p className="text-[#C2734F] text-sm mt-2" data-testid="contact-postal-error">
                          {t('rsvpPostalAddressRequired')}
                        </p>
                      )}
                    </div>

                    {/* Submit + Cancel */}
                    <div className="text-center mt-8 flex items-center justify-center gap-6">
                      {contactHadPrevious && (
                        <button
                          type="button"
                          onClick={handleContactCancel}
                          className="text-sm text-[#999] hover:text-[#2D2A26] transition-colors"
                          data-testid="contact-cancel-button"
                        >
                          {t('rsvpCancel')}
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={contactSubmitting}
                        className="inline-flex items-center gap-2 bg-[#B5985A] text-white px-8 py-3 rounded hover:bg-[#9A7F48] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="contact-submit-button"
                      >
                        <Send className="w-4 h-4" />
                        {contactSubmitting ? t('rsvpContactSubmitting') : t('rsvpContactSubmit')}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>

            {/* ── Section 2: Attendance ── */}
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm p-8 sm:p-10" data-testid="attendance-section">
              <AnimatePresence mode="wait">
                {/* Display state */}
                {attendanceMode === 'display' && rsvpData && (
                  <motion.div
                    key="attendance-display"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="attendance-display"
                  >
                    <SectionHeader
                      title={t('rsvpAttendanceTitle')}
                      action={t('rsvpModify')}
                      onAction={handleAttendanceModify}
                    />

                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 rounded-full bg-[#B5985A] flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-[#69635A] text-sm">{t('rsvpAttendanceConfirmed')}</p>
                    </div>

                    <div className="mb-4">
                      <AttendanceBadge label={t('rsvpFridayShort')} value={rsvpData.friday} />
                      <AttendanceBadge label={t('rsvpSaturdayShort')} value={rsvpData.saturday} />
                      <AttendanceBadge label={t('rsvpSundayShort')} value={rsvpData.sunday} />
                    </div>

                    {(rsvpData.dietary_restrictions || rsvpData.special_requests || rsvpData.questions) && (
                      <div>
                        {rsvpData.dietary_restrictions && (
                          <DisplayRow
                            label={t('rsvpDietaryRestrictions')}
                            value={rsvpData.dietary_restrictions}
                          />
                        )}
                        {rsvpData.special_requests && (
                          <DisplayRow
                            label={t('rsvpSpecialRequests')}
                            value={rsvpData.special_requests}
                          />
                        )}
                        {rsvpData.questions && (
                          <DisplayRow label={t('rsvpQuestions')} value={rsvpData.questions} />
                        )}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Form state */}
                {attendanceMode === 'form' && (
                  <motion.form
                    key="attendance-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={handleAttendanceSubmit}
                    noValidate
                    data-testid="rsvp-form"
                  >
                    <SectionHeader title={t('rsvpAttendanceTitle')} />

                    <p className="text-center font-serif text-2xl sm:text-3xl text-[#2D2A26] mb-2">
                      {guest?.first_name} {guest?.last_name}
                    </p>
                    <p className="font-serif text-[#C2734F] text-sm mb-6">{t('rsvpRequiredNote')}</p>

                    <RadioGroup
                      name="friday"
                      value={attendanceFormData.friday}
                      onChange={handleRadioChange}
                      label={t('rsvpFriday')}
                      options={twoOptions}
                      error={dayErrors.friday}
                    />
                    <RadioGroup
                      name="saturday"
                      value={attendanceFormData.saturday}
                      onChange={handleRadioChange}
                      label={t('rsvpSaturday')}
                      options={twoOptions}
                      error={dayErrors.saturday}
                    />
                    <RadioGroup
                      name="sunday"
                      value={attendanceFormData.sunday}
                      onChange={handleRadioChange}
                      label={t('rsvpSunday')}
                      options={twoOptions}
                      error={dayErrors.sunday}
                    />

                    <div className="w-8 h-0.5 bg-[#E5E5E5] mx-auto mb-10" />

                    {/* Dietary Restrictions */}
                    <div className="mb-6">
                      <label className="block font-serif text-lg text-[#2D2A26] mb-2">
                        {t('rsvpDietaryRestrictions')} <span className="text-[#C2734F]">*</span>
                      </label>
                      <textarea
                        value={attendanceFormData.dietaryRestrictions}
                        onChange={(e) => {
                          setAttendanceFormData((prev) => ({ ...prev, dietaryRestrictions: e.target.value }));
                          if (dietaryError) setDietaryError(false);
                        }}
                        placeholder={t('rsvpDietaryRestrictionsPlaceholder')}
                        rows={2}
                        className={`w-full border-b py-3 px-1 text-[#2D2A26] placeholder-[#BBB] focus:outline-none transition-colors bg-transparent resize-none ${
                          dietaryError
                            ? 'border-[#C2734F] focus:border-[#C2734F]'
                            : 'border-[#DDD] focus:border-[#B5985A]'
                        }`}
                        data-testid="rsvp-dietary-input"
                        aria-invalid={dietaryError}
                      />
                      {dietaryError && (
                        <p className="text-[#C2734F] text-sm mt-2">{t('rsvpDietaryRequired')}</p>
                      )}
                    </div>

                    {/* Special Requests */}
                    <div className="mb-6">
                      <label className="block font-serif text-lg text-[#2D2A26] mb-2">
                        {t('rsvpSpecialRequests')}
                      </label>
                      <textarea
                        value={attendanceFormData.specialRequests}
                        onChange={(e) =>
                          setAttendanceFormData((prev) => ({ ...prev, specialRequests: e.target.value }))
                        }
                        placeholder={t('rsvpSpecialRequestsPlaceholder')}
                        rows={2}
                        className="w-full border-b border-[#DDD] py-3 px-1 text-[#2D2A26] placeholder-[#BBB] focus:outline-none focus:border-[#B5985A] transition-colors bg-transparent resize-none"
                        data-testid="rsvp-special-requests-input"
                      />
                    </div>

                    {/* Questions */}
                    <div className="mb-6">
                      <label className="block font-serif text-lg text-[#2D2A26] mb-2">
                        {t('rsvpQuestions')}
                      </label>
                      <textarea
                        value={attendanceFormData.questions}
                        onChange={(e) =>
                          setAttendanceFormData((prev) => ({ ...prev, questions: e.target.value }))
                        }
                        placeholder={t('rsvpQuestionsPlaceholder')}
                        rows={1}
                        className="w-full border-b border-[#DDD] py-3 px-1 text-[#2D2A26] placeholder-[#BBB] focus:outline-none focus:border-[#B5985A] transition-colors bg-transparent resize-none"
                        data-testid="rsvp-questions-input"
                      />
                    </div>

                    {/* Submit + Cancel */}
                    <div className="text-center mt-10 flex items-center justify-center gap-6">
                      {rsvpData && (rsvpData.friday || rsvpData.saturday || rsvpData.sunday) && (
                        <button
                          type="button"
                          onClick={handleAttendanceCancel}
                          className="text-sm text-[#999] hover:text-[#2D2A26] transition-colors"
                          data-testid="rsvp-cancel-button"
                        >
                          {t('rsvpCancel')}
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={attendanceSubmitting}
                        className="inline-flex items-center gap-2 bg-[#B5985A] text-white px-10 py-3 rounded hover:bg-[#9A7F48] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="rsvp-submit-button"
                      >
                        <Send className="w-4 h-4" />
                        {attendanceSubmitting ? t('rsvpSubmitting') : t('rsvpSubmit')}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
